/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { getCouponCodeData } from '../services/coupon';
import { ICoupon, IGiftCard } from '../../interfaces/coupon';

export interface UseUniversalCouponInputProps {
  initialCouponCode?: string;
  plan: string;
  postalCode?: string;
  state?: string;
  country?: string;
}
const COUPON_ID = 'coupon';
const GIFT_CARD_ID = 'giftcard';

const useUniversalCouponInput = ({
  initialCouponCode,
  plan,
  postalCode,
  state,
  country,
}: UseUniversalCouponInputProps) => {
  const [couponData, setCouponData] = useState<ICoupon | IGiftCard | null>(null);
  const [coupon, setCoupon] = useState<string>(initialCouponCode ?? '');
  const [error, setError] = useState<boolean | null>(null);
  const [promoCodeType, setPromoCodeType] = useState<'coupon' | 'giftcard' | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleOnChange = (e?: React.ChangeEvent<HTMLInputElement>, couponCode?: string) => {
    const newCoupon = e ? e.target.value : couponCode || '';
    setCoupon(newCoupon);
    setError(null);
  };

  const handleStateBeforeFetch = () => {
    setError(null);
    setLoading(true);
    setCouponData(null);
    setPromoCodeType(null);
  };

  const handleCallCouponApi = async (couponCode?: string) => {
    handleStateBeforeFetch();
    const activeCoupon = couponCode || coupon;
    if (activeCoupon.length < 1) {
      setLoading(false);
      return;
    }
    if (couponCode) setCoupon(couponCode);
    try {
      const response = await getCouponCodeData({
        couponCode: activeCoupon,
        plan,
        postalCode,
        state,
        country,
      });

      const isCoupon = !!response.coupon;
      setCouponData(isCoupon ? response.coupon : response.giftCard);
      setPromoCodeType(isCoupon ? COUPON_ID : GIFT_CARD_ID);
    } catch (err) {
      setCouponData(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (postalCode && postalCode.length > 4 && coupon) handleCallCouponApi();
  }, [postalCode]);

  useEffect(() => {
    if (coupon) handleCallCouponApi();
  }, [plan]);

  useEffect(() => {
    if (initialCouponCode) handleCallCouponApi();
  }, [initialCouponCode]);

  return {
    couponData,
    error,
    promoCodeType,
    handleCallCouponApi,
    loading,
    coupon,
    handleOnChange,
  };
};

export type UseUniversalCouponInputReturn = ReturnType<typeof useUniversalCouponInput>;
export default useUniversalCouponInput;
